/**
 * toolFormatters — Human-readable formatting for MCP tool inputs and results.
 *
 * Transforms raw JSON into cleaner displays for known Jam SQL MCP tools.
 */

import { isMcpTool, stripMcpPrefix } from './toolMeta';

/** Format tool input as human-readable key-value pairs for known MCP tools. */
export function formatToolInput(rawName: string, input: Record<string, unknown>): { formatted: string; isFormatted: boolean } {
  if (!isMcpTool(rawName)) return { formatted: JSON.stringify(input, null, 2), isFormatted: false };

  const name = stripMcpPrefix(rawName);
  const entries = Object.entries(input).filter(([, v]) => v !== undefined && v !== null);

  if (entries.length === 0) return { formatted: '(no parameters)', isFormatted: true };

  // Special formatting for SQL-heavy tools
  if ((name === 'query_execute' || name === 'ui_set_editor_text') && input.sql) {
    const otherEntries = entries.filter(([k]) => k !== 'sql');
    const parts: string[] = [];
    for (const [key, val] of otherEntries) {
      parts.push(`${formatKey(key)}: ${formatValue(val)}`);
    }
    parts.push(`SQL:\n${String(input.sql)}`);
    return { formatted: parts.join('\n'), isFormatted: true };
  }

  // Generic key-value formatting for all other MCP tools
  const lines = entries.map(([key, val]) => `${formatKey(key)}: ${formatValue(val)}`);
  return { formatted: lines.join('\n'), isFormatted: true };
}

/** Format tool result content for known MCP tools. */
export function formatToolResult(rawName: string, content: string, isError: boolean): { formatted: string; isFormatted: boolean } {
  if (!isMcpTool(rawName)) return { formatted: content, isFormatted: false };

  // MCP results are often wrapped in [{"type":"text","text":"..."}] format
  const unwrapped = unwrapMcpResult(content);
  if (!unwrapped) return { formatted: content, isFormatted: false };

  if (isError) return { formatted: unwrapped, isFormatted: true };

  // Try to parse as JSON for pretty display
  try {
    const parsed = JSON.parse(unwrapped);
    if (typeof parsed === 'object' && parsed !== null) {
      return { formatted: formatResultObject(parsed), isFormatted: true };
    }
  } catch {
    // Not JSON — return as plain text
  }

  return { formatted: unwrapped, isFormatted: true };
}

/** Unwrap MCP result from [{"type":"text","text":"..."}] envelope. */
function unwrapMcpResult(content: string): string | null {
  try {
    const parsed = JSON.parse(content);
    if (Array.isArray(parsed)) {
      const texts = parsed
        .filter((item: unknown) => {
          const obj = item as Record<string, unknown>;
          return obj && obj.type === 'text' && typeof obj.text === 'string';
        })
        .map((item: unknown) => (item as { text: string }).text);
      if (texts.length > 0) return texts.join('\n');
    }
  } catch {
    // Not the MCP array format
  }
  return null;
}

/** Format a result object as clean key-value output. */
function formatResultObject(obj: unknown): string {
  if (typeof obj !== 'object' || obj === null) return String(obj);

  const record = obj as Record<string, unknown>;

  // Handle { success: true/false, ... } pattern
  if ('success' in record) {
    const entries = Object.entries(record);
    return entries.map(([key, val]) => {
      if (typeof val === 'object' && val !== null) {
        return `${formatKey(key)}:\n${JSON.stringify(val, null, 2)}`;
      }
      return `${formatKey(key)}: ${formatValue(val)}`;
    }).join('\n');
  }

  // Handle arrays (e.g. table lists, column lists)
  if (Array.isArray(obj)) {
    if (obj.length === 0) return '(empty)';
    if (obj.length <= 20 && obj.every((item) => typeof item === 'string')) {
      return obj.join(', ');
    }
    return JSON.stringify(obj, null, 2);
  }

  return JSON.stringify(obj, null, 2);
}

/** Convert camelCase/snake_case key to readable label. */
function formatKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bSql\b/g, 'SQL')
    .replace(/\bId\b/g, 'ID')
    .replace(/\bUrl\b/g, 'URL');
}

/** Format a value for display. */
function formatValue(val: unknown): string {
  if (val === true) return 'Yes';
  if (val === false) return 'No';
  if (val === null || val === undefined) return '-';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}
